import { requiresApproval, } from './approval.js';
/** Board order, left to right. */
export const BOARD_BUCKET_IDS = [
    'backlog',
    'approved',
    'blocked',
    'in_progress',
    'completed',
    'not_approved',
];
export const BOARD_BUCKET_LABELS = {
    backlog: 'Backlog',
    approved: 'Approved',
    blocked: 'Blocked',
    in_progress: 'In progress',
    completed: 'Completed',
    not_approved: 'Not approved',
};
export const BOARD_BUCKETS = [
    {
        id: 'backlog',
        label: 'Backlog',
        meaning: 'Not cleared to start. Features await a decision here.',
    },
    { id: 'approved', label: 'Approved', meaning: 'Cleared and queued.' },
    {
        id: 'blocked',
        label: 'Blocked',
        meaning: 'Stopped until the client answers a question staff raised.',
    },
    { id: 'in_progress', label: 'In progress', meaning: 'Work underway.' },
    { id: 'completed', label: 'Completed', meaning: 'Delivered.' },
    { id: 'not_approved', label: 'Not approved', meaning: 'Declined.' },
];
/**
 * The single source of truth for board membership. The bucket is DERIVED, never stored:
 * a portal-side status column would immediately drift from GitHub.
 *
 * Two governing rules, in order:
 *  1. Approval governs Backlog -> Approved -> Not approved, and nothing else.
 *  2. The GitHub issue state governs everything after the gate.
 */
export function deriveBucket(input) {
    const gated = requiresApproval({
        kind: input.kind,
        imported: input.imported,
        issueState: input.issueState,
    });
    // Declined is terminal. Closing a declined issue upstream is cleanup, and must never be
    // re-read as "Delivered" — that is the difference between a refused quote and an invoice.
    if (gated && input.approvalStatus === 'not_approved')
        return 'not_approved';
    // Closed means finished, whatever the portal thinks. An item left in Backlog after its
    // issue closed would ask the client, forever, to decide on work that no longer exists.
    if (input.issueState === 'closed')
        return 'completed';
    if (gated && input.approvalStatus !== 'approved')
        return 'backlog';
    // Not gated is not the same as cleared. An unclassified request raised through the portal
    // is waiting on STAFF, not on the client, so it shows no decision control — but it must
    // not sit in Approved either: if triage turns it into a feature, billable work would have
    // read as queued and ready the whole time. Backlog is the honest place for it.
    if (input.kind === 'needs_triage' && input.imported !== true)
        return 'backlog';
    /*
     * STOPPED, WAITING ON THE CLIENT. Deliberately BELOW both Backlog arms and above In progress,
     * and every part of that position was chosen rather than fallen into.
     *
     * Below Backlog, because a request that has not been decided yet is ALREADY waiting on the
     * client and says so by being in Backlog — that is the lane a client goes to in order to
     * approve things, and pulling a card out of it to say "waiting on the client" a second time
     * would move it away from the control that unblocks it.
     *
     * Above In progress, because work that is stopped is not work underway. A card carrying an
     * unanswered question read as In progress on every board and in every count, which is the
     * one thing a status column must never do: report motion where there is none.
     *
     * Closed work never reaches here — the Completed arm above is unconditional — so a question
     * left raised on a delivered issue cannot strand its card, exactly as `deriveAttention`
     * suppresses the same stale flag rather than needing a cleanup job to remove it.
     */
    if (input.attentionRequestedAt)
        return 'blocked';
    // Past the gate and still open. EITHER signal means somebody is on it: GitHub's assignee, or
    // a staff member having said so in Portal. ORed rather than ANDed because the two can
    // legitimately disagree — work coordinated here and never assigned upstream, or assigned
    // upstream and never marked here — and the failure worth avoiding is a card that is being
    // worked on and does not say so.
    return input.assigneeLogin || input.startedAt ? 'in_progress' : 'approved';
}
//# sourceMappingURL=buckets.js.map